let _boundaryCheckingState = true; // 是否进行越界检查的全局开关

/**
 * 把错误的数据转正
 * @private
 * @example strip(0.09999999999999998)=0.1
 */
function strip(num: number, precision = 15) {
  return +parseFloat(Number(num).toPrecision(precision));
}

/**
 * Return digits length of a number
 * @private
 * @param {*number} num Input number
 */
function digitLength(num: number) {
  // Get digit length of e
  const eSplit = num.toString().split(/[eE]/);
  const len = (eSplit[0].split('.')[1] || '').length - +(eSplit[1] || 0);
  return len > 0 ? len : 0;
}

/**
 * 把小数转成整数,如果是小数则放大成整数
 * @private
 * @param {*number} num 输入数
 */
function float2Fixed(num: number) {
  if (num.toString().indexOf('e') === -1) {
    return Number(num.toString().replace('.', ''));
  }
  const dLen = digitLength(num);
  return dLen > 0 ? strip(Number(num) * Math.pow(10, dLen)) : Number(num);
}

/**
 * 检测数字是否越界，如果越界给出提示
 * @private
 * @param {*number} num 输入数
 */
function checkBoundary(num: number) {
  if (_boundaryCheckingState) {
    if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
      console.warn(`${num} 超出了精度限制，结果可能不正确`);
    }
  }
}

/**
 * 把递归操作扁平迭代化
 * @param {number[]} arr 要操作的数字数组
 * @param {function} operation 迭代操作
 * @private
 */
function iteratorOperation(arr: any[], operation: CallableFunction) {
  const [num1, num2, ...others] = arr;
  let res = operation(num1, num2);

  others.forEach((num) => {
    res = operation(res, num);
  });

  return res;
}

/**
 * 高精度乘法
 * 支持多个参数连续乘法，自动处理小数精度
 * @export
 * @param {...number[]} nums 参与运算的数字
 * @returns {number} 结果
 */
export function mul(...nums: any[]) {
  if (nums.length > 2) {
    return iteratorOperation(nums, mul);
  }

  const [num1, num2] = nums;
  const num1Changed = float2Fixed(num1);
  const num2Changed = float2Fixed(num2);
  const baseNum = digitLength(num1) + digitLength(num2);
  const leftValue = num1Changed * num2Changed;

  checkBoundary(leftValue);

  return leftValue / Math.pow(10, baseNum);
}

/**
 * 高精度加法
 * 支持多个参数连续加法，自动处理小数精度
 * @export
 * @param {...number[]} nums 参与运算的数字
 * @returns {number} 结果
 */
export function add(...nums: any[]) {
  if (nums.length > 2) {
    return iteratorOperation(nums, add);
  }

  const [num1, num2] = nums;
  // 取最大的小数位
  const baseNum = Math.pow(10, Math.max(digitLength(num1), digitLength(num2)));
  // 把小数都转为整数然后再计算
  return (mul(num1, baseNum) + mul(num2, baseNum)) / baseNum;
}

/**
 * 高精度减法
 * 支持多个参数连续减法，自动处理小数精度
 * @export
 * @param  nums 参与运算的数字
 * @returns  结果
 */
export function sub(...nums: any[]) {
  if (nums.length > 2) {
    return iteratorOperation(nums, sub);
  }

  const [num1, num2] = nums;
  const baseNum = Math.pow(10, Math.max(digitLength(num1), digitLength(num2)));
  return (mul(num1, baseNum) - mul(num2, baseNum)) / baseNum;
}

/**
 * 高精度除法
 * 支持多个参数连续除法，自动处理小数精度
 * @export
 * @param {...number[]} nums 参与运算的数字
 * @returns {number} 结果
 */
export function div(...nums: any[]) {
  if (nums.length > 2) {
    return iteratorOperation(nums, div);
  }
  const [num1, num2] = nums;
  const num1Changed = float2Fixed(num1);
  const num2Changed = float2Fixed(num2);
  checkBoundary(num1Changed);
  checkBoundary(num2Changed);
  // 用 strip 修正精度
  return mul(num1Changed / num2Changed, strip(Math.pow(10, digitLength(num2) - digitLength(num1))));
}

/**
 * 高精度四舍五入
 * @export
 * @param {number} num 需要处理的数字
 * @param {number} ratio 保留的小数位数
 * @returns {number} 结果
 */
export function round(num: number, ratio: number) {
  const base = Math.pow(10, ratio);
  let result = div(Math.round(Math.abs(mul(num, base))), base);
  if (num < 0 && result !== 0) {
    result = mul(result, -1);
  }
  return result;
}

/**
 * 设置是否进行边界检查，默认开启
 * @param {boolean} flag 标记开关，true 为开启，false 为关闭，默认为 true
 * @export
 */
export function enableBoundaryChecking(flag = true) {
  _boundaryCheckingState = flag;
}
